"use client";

import React, { useEffect, useRef, useState } from "react";
import { Separator } from "@/components/ui/separator";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
export function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
interface AnimatedCounterProps {
  end: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
}
function AnimatedCounter({
  end,
  suffix = "",
  prefix = "",
  duration = 2000
}: AnimatedCounterProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLSpanElement>({
    once: true,
    threshold: 0.5
  });
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    let startTimestamp: number | null = null;
    let animationFrameId: number;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      const easeProgress = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeProgress * end));
      if (progress < 1) {
        animationFrameId = window.requestAnimationFrame(step);
      } else {
        setCount(end);
      }
    };
    animationFrameId = window.requestAnimationFrame(step);
    return () => {
      if (animationFrameId) {
        window.cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isVisible, end, duration]);
  return <span ref={ref}>
            {prefix}
            {count}
            {suffix}
        </span>;
}
const metrics = [{
  id: 1,
  value: 500,
  suffix: "k+",
  label: "Bottles Diverted"
}, {
  id: 2,
  value: 12,
  suffix: "k",
  label: "Organic Acres"
}, {
  id: 3,
  value: 100,
  suffix: "%",
  label: "Fair Trade"
}, {
  id: 4,
  value: 250,
  suffix: "+",
  label: "Custom Teams"
}];
export default function TheGreenFootprint() {
  return <section data-section-id="161904" id="green-victory-stats" className="dark bg-background text-foreground py-24 relative overflow-hidden">
            {}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--color-primary)_0%,_transparent_70%)] opacity-[0.05] pointer-events-none" />

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col md:flex-row items-center justify-between">
                        {metrics.map((metric, i) => <div data-index={i} key={metric.id} className="flex flex-col md:flex-row items-center w-full md:w-auto flex-1">
                                <div className="flex-1 flex flex-col items-center text-center w-full py-10 md:py-4 px-4">
                                    <h3 className="text-5xl lg:text-7xl font-sans font-bold text-primary mb-4 tracking-tighter">
                                        <AnimatedCounter end={metric.value} suffix={metric.suffix} />
                                    </h3>
                                    <p className="text-foreground/60 font-sans font-medium uppercase tracking-widest text-xs lg:text-sm">
                                        {metric.label}
                                    </p>
                                </div>

                                {i < metrics.length - 1 && <div className="w-full md:w-auto flex justify-center items-center self-stretch">
                                        {}
                                        <Separator orientation="vertical" className="hidden md:block h-24 bg-border/20 w-px" />
                                        {}
                                        <Separator orientation="horizontal" className="block md:hidden w-24 bg-border/20 h-px" />
                                    </div>}
                            </div>)}
                    </div>
                </div>
            </div>
        </section>;
}